/**
 * What: Renders classic Polaris page-header action descriptors (`{content, onAction, url, ...}`)
 * as slotted `<s-button>` children for `<s-page>`, which takes primary/secondary/breadcrumb
 * actions as elements rather than action objects.
 * Transport / data-flow: Pure presentational mapping; callers keep building the same descriptor
 * shapes classic `Page` accepted.
 */
import type { ReactNode } from 'react'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../shopifyUiTracking'

export type ActionDescriptor = {
  content?: string
  onAction?: () => void
  url?: string
  external?: boolean
  target?: string
  disabled?: boolean
  destructive?: boolean
  loading?: boolean
  accessibilityLabel?: string
  /**
   * Why the action is disabled (or other explanatory context). Classic Polaris rendered this as
   * a hover tooltip via `TooltipAction`; `s-button` has no equivalent prop (no generic HTML
   * attribute passthrough, and no confirmed-stable tooltip-association API), so it's folded into
   * the visible label instead — a merchant seeing a disabled button should not see zero explanation.
   */
  helpText?: ReactNode
}

type ActionSlot = 'primary-action' | 'secondary-actions' | 'breadcrumb-actions'
type ShopifyButtonIcon = HTMLElementTagNameMap['s-button']['icon']

function ShopifyActionButton({
  slot,
  action,
  icon,
}: {
  slot: ActionSlot
  action: ActionDescriptor
  icon?: ShopifyButtonIcon
}) {
  const shopify = useAppBridge()
  const handleClick = () => {
    if (slot === 'breadcrumb-actions') return trackShopifyAction(shopify, 'navigate-studies', () => action.onAction?.())
    if (!action.url) action.onAction?.()
  }
  return (
    <s-button
      slot={slot}
      href={action.url}
      target={action.external ? '_blank' : (action.target as '_blank' | '_self' | '_top' | undefined)}
      disabled={action.disabled}
      loading={action.loading}
      tone={action.destructive ? 'critical' : undefined}
      icon={icon}
      accessibilityLabel={action.accessibilityLabel}
      onClick={handleClick}
    >
      {action.content}
      {action.helpText ? <> ({action.helpText})</> : null}
    </s-button>
  )
}

/** One `<s-button slot="primary-action">`, or nothing when no primary action is set. */
export function ShopifyPrimaryActionButton({ action }: { action?: ActionDescriptor }) {
  if (!action) return null
  return <ShopifyActionButton slot="primary-action" action={action} />
}

/** One `<s-button slot="secondary-actions">` per descriptor. */
export function ShopifySecondaryActionButtons({ actions }: { actions?: ActionDescriptor[] }) {
  if (!actions?.length) return null
  return (
    <>
      {actions.map((action, index) => (
        <ShopifyActionButton key={action.content ?? index} slot="secondary-actions" action={action} />
      ))}
    </>
  )
}

/** One `<s-button slot="breadcrumb-actions">` styled as a back link — `s-page` has no dedicated backAction prop. */
export function ShopifyBackActionButton({ action }: { action?: ActionDescriptor }) {
  if (!action) return null
  return <ShopifyActionButton slot="breadcrumb-actions" action={action} icon="arrow-left" />
}
